import { useQuery } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { selectGetUserActivePermissionGroup } from '@/features/auth/stores/auth/auth.selectors'
import authStore from '@/features/auth/stores/auth/auth.store'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import { selectGetSelectedGroupId } from '@/features/groups/stores/groups/groups.selectors'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import { getUserGroupRoles, V1_USER_GROUP_ROLES_QUERY_KEY } from '@/features/users/api'

function useGroupRoles({ groupIdRef }: { groupIdRef?: ComputedRef<string | null | undefined> } = {}) {
  const { accountRef } = useAccount()
  const selectedGroupIdRef = selectGetSelectedGroupId(groupsStore)
  const userActivePermissionGroupRef = selectGetUserActivePermissionGroup(authStore)
  const usedGroupIdRef = computed(() => groupIdRef?.value || selectedGroupIdRef.value!)
  const accountIdRef = computed(() => accountRef.value?.id)
  const isGroupQueryEnabledRef = computed(
    () =>
      !!usedGroupIdRef.value &&
      !!accountIdRef?.value &&
      // should check for the user permission group to avoid fetching the group roles for the admin
      userActivePermissionGroupRef.value === PermissionsGroupsEnumType.USER,
  )
  const queryResult = useQuery({
    queryKey: [V1_USER_GROUP_ROLES_QUERY_KEY, { groupId: usedGroupIdRef, userId: accountIdRef }],
    queryFn: () => getUserGroupRoles({ groupId: usedGroupIdRef.value!, userId: accountIdRef.value! }),
    enabled: isGroupQueryEnabledRef,
  })

  // merging all data from the queries (that constitute the account data)
  const groupRolesDataRef = computed(() => {
    return queryResult.data.value?.roles
  })

  // check if any query is pending
  const isPendingRef = computed(() => queryResult.isPending.value)

  return {
    groupRolesRef: groupRolesDataRef,
    isPendingRef,
  }
}

export default useGroupRoles
